import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { useStudio } from '@/features/studio/contexts/StudioContext';

export interface StudioMember {
  id: string;
  user_id: string;
  role: 'resident' | 'guest';
  status: 'active' | 'archived';
  invited_at: string;
  joined_at: string | null;
  expires_at: string | null;
  display_name: string | null;
  email: string | null;
}

export function useStudioMembers() {
  const { membership } = useStudio();
  const studioId = membership?.isOwner ? membership.studioId : null;

  return useQuery({
    queryKey: ['studio-members', studioId],
    enabled: !!studioId,
    queryFn: async (): Promise<StudioMember[]> => {
      // No direct FK between studio_members.user_id and profiles — two separate queries
      const { data, error } = await supabase
        .from('studio_members')
        .select('id, user_id, role, status, invited_at, joined_at, expires_at')
        .eq('studio_id', studioId!)
        .order('invited_at', { ascending: false });
      if (error) throw error;
      if (!data || data.length === 0) return [];

      const userIds = data.map((m: any) => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, email')
        .in('user_id', userIds);
      const profileMap: Record<string, { display_name: string | null; email: string | null }> = {};
      (profiles || []).forEach((p: any) => { profileMap[p.user_id] = { display_name: p.display_name, email: p.email }; });

      return data.map((m: any) => ({
        id: m.id,
        user_id: m.user_id,
        role: m.role as 'resident' | 'guest',
        status: m.status as 'active' | 'archived',
        invited_at: m.invited_at,
        joined_at: m.joined_at,
        expires_at: m.expires_at,
        display_name: profileMap[m.user_id]?.display_name ?? null,
        email: profileMap[m.user_id]?.email ?? null,
      }));
    },
  });
}

export function useCreateInvitation() {
  const { user } = useAuth();
  const { membership } = useStudio();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ role }: { role: 'resident' | 'guest' }): Promise<string> => {
      if (!membership?.isOwner) throw new Error('Not a studio owner');
      const { data, error } = await supabase
        .from('studio_invitations')
        .insert({ studio_id: membership.studioId, role, created_by: user!.id })
        .select('token')
        .single();
      if (error) throw error;
      return `${window.location.origin}/join?token=${data.token}`;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['studio-invitations'] });
      qc.invalidateQueries({ queryKey: ['studio-members'] });
    },
  });
}

export function useArchiveMember() {
  const { membership } = useStudio();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('studio_members')
        .update({ status: 'archived' })
        .eq('id', memberId)
        .eq('studio_id', membership!.studioId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio-members'] }),
  });
}

export function useRestoreMember() {
  const { membership } = useStudio();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from('studio_members')
        .update({ status: 'active' })
        .eq('id', memberId)
        .eq('studio_id', membership!.studioId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['studio-members'] }),
  });
}

export function useStudioAppointments() {
  const { membership } = useStudio();
  const studioId = membership?.isOwner ? membership.studioId : null;

  return useQuery({
    queryKey: ['studio-appointments', studioId],
    enabled: !!studioId,
    queryFn: async () => {
      const { data: members, error: mErr } = await supabase
        .from('studio_members')
        .select('user_id')
        .eq('studio_id', studioId!)
        .eq('status', 'active');
      if (mErr) throw mErr;
      if (!members || members.length === 0) return [];

      const memberIds = members.map((m: any) => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', memberIds);
      const nameMap: Record<string, string> = {};
      (profiles || []).forEach((p: any) => { nameMap[p.user_id] = p.display_name ?? p.user_id.slice(0, 8); });

      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .in('owner_id', memberIds)
        .gte('date', today)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(50);
      if (error) throw error;

      return (data || []).map((a: any) => ({ ...a, artist_name: nameMap[a.owner_id] ?? a.owner_id.slice(0, 8) }));
    },
  });
}
